import { extent } from 'd3-array'
import { DataEvent, MetaConfig, StartInfo, TemporalEvent, TemporalState } from './types'
import crop from './crop'

const TimSort = require('timsort')

function y(row: [number, number]): number {
    return row[1]
}

function sortCompare(a: [number, number], b: [number, number]): number {
    return a[0] - b[0]
}

function checkSpan(timestamp: number, span: [number, number]): boolean {
    return timestamp >= span[0] && timestamp <= span[1]
}

function realtimeSpan(span: { normalized: string }): boolean {
    const normalized = span?.normalized ?? ''
    if (normalized.includes('now') && !normalized.includes('shifted')) {
        return true
    }

    return false
}

/**
 * Merge config from multiple layers into a single `MetaConfig`
 * @param info
 * @param config
 */
export function mergeMetaConfig(info: StartInfo, config: MetaConfig): MetaConfig {
    if (info === undefined) {
        return config
    }
    const fields = ['__color', '__description', '__precision', '__uom']

    return fields
        .filter(field => info[field])
        .reduce((config, field) => {
            return Object.entries(info[field]).reduce((config, [asset, value]) => {
                return {
                    ...config,
                    [asset]: {
                        ...(config[asset] ?? {}),
                        [field.substr(2)]: value
                    }
                }
            }, config)
        }, config)
}

export default function reducer(
    state: TemporalState = {
        realtime: false,
        series: {},
        config: {},
        span: [0, Number.MAX_SAFE_INTEGER]
    },
    event: FlowMessage<LiveEvent<TemporalEvent>>
): TemporalState {
    switch (event?.type) {
        case 'start':
            if (event.layer === 0) {
                return {
                    series: {},
                    span: [0, Number.MAX_SAFE_INTEGER],
                    realtime: realtimeSpan(event.events?.info?.span),
                    config: mergeMetaConfig(event.events?.info, state.config)
                }
            } else {
                return {
                    ...state,
                    config: mergeMetaConfig(event.events?.info, state.config)
                }
            }
        case 'span':
            // nothing changed. skip it.
            if (state.span[0] === event.events.begin && state.span[1] === event.events.end) {
                return state
            }
            state.span = [event.events.begin, event.events.end]

            // re-calculate min-max to each channel
            for (const [mnemonic, channel] of Object.entries(state.series)) {
                // remove events out of span.
                if (channel.data.length > 0) {
                    channel.data = crop(channel.data, state.span[0], state.span[1])

                    // calculate new domain.
                    channel.domain = channel.data.length > 0 ? extent(channel.data, y) : [0, 0]
                } else {
                    channel.domain = [0, 0]
                }
            }

            return state
        case 'event':
            const unsorted = new Set<string>()

            event.events.forEach(ev => {
                const { timestamp, mnemonic } = ev
                let calculatedChannelName: string

                // TODO: check count: number events
                if (!mnemonic) {
                    calculatedChannelName = getCalculatedChannelName(state.config, ev)
                }

                if (!mnemonic && !calculatedChannelName) {
                    return
                }

                const value = ev?.value ?? ev[calculatedChannelName]
                const channelName = mnemonic ?? calculatedChannelName
                const serie = state.series[channelName]
                // out-of-span: don't append on state.
                const end = state.span[1] + (state.span[1] - state.span[0]) * 0.1

                if (timestamp < state.span[0] || timestamp > end) {
                    return
                }

                if (!serie) {
                    state.series[channelName] = {
                        data: checkSpan(timestamp, state.span) ? [[timestamp, value]] : [],
                        domain: [value, value]
                    }
                } else {
                    const data = serie.data
                    // out-of-order events, please sort it at the end.
                    if (data.length > 0 && ev.timestamp < data[data.length - 1][0]) {
                        unsorted.add(channelName)
                    }
                    // update domain.
                    if (value < serie.domain[0]) {
                        serie.domain[0] = value
                    } else if (value > serie.domain[1]) {
                        serie.domain[1] = value
                    }
                    data.push([ev.timestamp, value])
                }
            })
            // sort all unsorted datasets
            unsorted.forEach(mnemonic => {
                TimSort.sort(state.series[mnemonic].data, sortCompare)
            })

            return { ...state }
        case 'gaps':
            // TODO: disable gap report for labels.
            for (const gap of event.events.gaps) {
                if (gap.field === 'mnemonic') {
                    const qualifier = gap.groups[0]
                    const serie = state.series[qualifier]
                    if (serie) {
                        // removing previous gaps
                        serie.data.forEach((point, index) => {
                            if (point[1] === null) {
                                serie.data.splice(index, 1)
                            }
                        })
                        // Add new gaps
                        for (const data of gap.gaps) {
                            // Should be validate if the begin and end timestamps
                            // is inside of a span?
                            const [begin, end] = data

                            // gap from minus infinity to the edge of the span should not be inserted in the series
                            // state.span[0] = min
                            if (begin == null && end != null && end <= state.span[0]) {
                                // return state
                                continue
                            }

                            // gap from the edge of the span to infinity should not be inserted in the series
                            // state.span[1] = max
                            if (begin != null && end == null && begin >= state.span[1]) {
                                // return state
                                continue
                            }

                            if (begin != null) {
                                serie.data.push([begin + Number.EPSILON, null])
                            }

                            if (end != null) {
                                serie.data.push([end + Number.EPSILON, null])
                            }
                        }
                        TimSort.sort(serie.data, sortCompare)
                    }
                }
            }

            return state

        default:
            return state
    }
}

function getCalculatedChannelName(config: MetaConfig, event: DataEvent): string {
    return Object.keys(config).find(channel => channel in event)
}
